// Schemas de Cuestionarios Clínicos
// Formato JSON para definir preguntas de diferentes tipos

export const QUESTION_TYPES = {
  RADIO: 'radio',
  TEXT: 'text',
  SCALE: 'scale',
  FACE_SCALE: 'face_scale',
  NUMBER: 'number',
  TEXTAREA: 'textarea',
};

// Cuestionario SF-12v1 (Short Form 12) - Versión en Español
// Algoritmo oficial NEMC/UCLA (Ware, Kosinski & Keller, 1996)
export const SF12_SCHEMA = {
  id: 'sf12',
  name: 'SF-12',
  fullName: 'Cuestionario de Salud SF-12v1',
  description: 'Cuestionario de 12 preguntas para evaluar la calidad de vida relacionada con la salud física y mental. Scoring oficial NEMC/UCLA con T-scores (media=50, SD=10).',
  estimatedTime: '3-5 minutos',
  category: 'salud_general',
  version: '1.0',
  scoring: {
    type: 'pcs_mcs',
    method: 'nemc_regression', // Algoritmo oficial con pesos de regresión
  },
  sections: [
    {
      id: 'salud_general',
      title: 'Salud General',
      questions: [
        {
          // Item 1 — GH (General Health) — RFIVEPT invertida
          id: 'sf1',
          text: 'En general, ¿diría que su salud es:',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Excelente' },
            { value: 2, label: 'Muy buena' },
            { value: 3, label: 'Buena' },
            { value: 4, label: 'Regular' },
            { value: 5, label: 'Mala' },
          ],
          scoring: { scale: 'GH1', transform: 'RFIVEPT' },
        },
      ],
    },
    {
      id: 'limitaciones_fisicas',
      title: 'Limitaciones en Actividades Físicas',
      description: 'Las siguientes preguntas se refieren a actividades o cosas que usted podría hacer en un día normal. Su salud actual, ¿le limita para hacer esas actividades o cosas? Si es así, ¿cuánto?',
      questions: [
        {
          // Item 2a — PF (Physical Functioning) — THREEPT
          id: 'sf2',
          text: 'Su salud, ¿le limita para hacer esfuerzos moderados, como mover una mesa, pasar la aspiradora, jugar a los bolos o caminar más de una hora?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Sí, me limita mucho' },
            { value: 2, label: 'Sí, me limita un poco' },
            { value: 3, label: 'No, no me limita' },
          ],
          scoring: { scale: 'PF02', transform: 'THREEPT' },
        },
        {
          // Item 2b — PF (Physical Functioning) — THREEPT
          id: 'sf3',
          text: 'Su salud, ¿le limita para subir varios pisos por la escalera?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Sí, me limita mucho' },
            { value: 2, label: 'Sí, me limita un poco' },
            { value: 3, label: 'No, no me limita' },
          ],
          scoring: { scale: 'PF04', transform: 'THREEPT' },
        },
      ],
    },
    {
      id: 'problemas_fisicos',
      title: 'Problemas en Actividades por Salud Física',
      description: '',
      questions: [
        {
          // Item 3a — RP (Role Physical) — TWOPT
          id: 'sf4',
          text: 'Durante las últimas 4 semanas, ¿hizo menos de lo que hubiera querido hacer a causa de su salud física?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Sí' },
            { value: 2, label: 'No' },
          ],
          scoring: { scale: 'RP2', transform: 'TWOPT' },
        },
        {
          // Item 3b — RP (Role Physical) — TWOPT
          id: 'sf5',
          text: 'Durante las últimas 4 semanas, ¿tuvo que dejar de hacer algunas tareas en su trabajo o en sus actividades cotidianas a causa de su salud física?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Sí' },
            { value: 2, label: 'No' },
          ],
          scoring: { scale: 'RP3', transform: 'TWOPT' },
        },
      ],
    },
    {
      id: 'problemas_emocionales',
      title: 'Problemas en Actividades por Salud Emocional',
      description: '',
      questions: [
        {
          // Item 4a — RE (Role Emotional) — TWOPT
          id: 'sf6',
          text: 'Durante las últimas 4 semanas, ¿hizo menos de lo que hubiera querido hacer a causa de algún problema emocional?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Sí' },
            { value: 2, label: 'No' },
          ],
          scoring: { scale: 'RE2', transform: 'TWOPT' },
        },
        {
          // Item 4b — RE (Role Emotional) — TWOPT
          id: 'sf7',
          text: 'Durante las últimas 4 semanas, ¿no hizo su trabajo o sus actividades cotidianas tan cuidadosamente como de costumbre a causa de algún problema emocional?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Sí' },
            { value: 2, label: 'No' },
          ],
          scoring: { scale: 'RE3', transform: 'TWOPT' },
        },
      ],
    },
    {
      id: 'dolor',
      title: 'Dolor',
      questions: [
        {
          // Item 5 — BP (Bodily Pain) — RFIVEPT invertida
          id: 'sf8',
          text: 'Durante las últimas 4 semanas, ¿hasta qué punto el dolor le ha dificultado su trabajo habitual (incluido el trabajo fuera de casa y las tareas domésticas)?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Nada' },
            { value: 2, label: 'Un poco' },
            { value: 3, label: 'Regular' },
            { value: 4, label: 'Bastante' },
            { value: 5, label: 'Mucho' },
          ],
          scoring: { scale: 'BP2', transform: 'RFIVEPT' },
        },
      ],
    },
    {
      id: 'bienestar_emocional',
      title: 'Bienestar Emocional',
      description: 'Las preguntas que siguen se refieren a cómo se ha sentido y cómo le han ido las cosas durante las últimas 4 semanas. En cada pregunta responda lo que se parezca más a cómo se ha sentido usted.',
      questions: [
        {
          // Item 6a = SAS I26 — "calm and peaceful" — EM3 — RSIXPT
          id: 'sf9',
          text: '¿Cuánto tiempo durante las últimas 4 semanas se sintió calmado/a y tranquilo/a?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Siempre' },
            { value: 2, label: 'Casi siempre' },
            { value: 3, label: 'Muchas veces' },
            { value: 4, label: 'Algunas veces' },
            { value: 5, label: 'Solo alguna vez' },
            { value: 6, label: 'Nunca' },
          ],
          scoring: { scale: 'EM3', transform: 'RSIXPT' },
        },
        {
          // Item 6b = SAS I27 — "lot of energy" — EN2 — RSIXPT
          id: 'sf10',
          text: '¿Cuánto tiempo durante las últimas 4 semanas tuvo mucha energía?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Siempre' },
            { value: 2, label: 'Casi siempre' },
            { value: 3, label: 'Muchas veces' },
            { value: 4, label: 'Algunas veces' },
            { value: 5, label: 'Solo alguna vez' },
            { value: 6, label: 'Nunca' },
          ],
          scoring: { scale: 'EN2', transform: 'RSIXPT' },
        },
        {
          // Item 6c = SAS I28 — "downhearted and blue" — EM4 — SIXPT
          id: 'sf11',
          text: '¿Cuánto tiempo durante las últimas 4 semanas se sintió desanimado/a y triste?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Siempre' },
            { value: 2, label: 'Casi siempre' },
            { value: 3, label: 'Muchas veces' },
            { value: 4, label: 'Algunas veces' },
            { value: 5, label: 'Solo alguna vez' },
            { value: 6, label: 'Nunca' },
          ],
          scoring: { scale: 'EM4', transform: 'SIXPT' },
        },
      ],
    },
    {
      id: 'funcion_social',
      title: 'Función Social',
      questions: [
        {
          // Item 7 — SF (Social Functioning) — FIVEPT directa
          id: 'sf12',
          text: 'Durante las últimas 4 semanas, ¿con qué frecuencia la salud física o los problemas emocionales le han dificultado sus actividades sociales (como visitar a los amigos o familiares)?',
          type: QUESTION_TYPES.RADIO,
          required: true,
          options: [
            { value: 1, label: 'Siempre' },
            { value: 2, label: 'Casi siempre' },
            { value: 3, label: 'Algunas veces' },
            { value: 4, label: 'Solo alguna vez' },
            { value: 5, label: 'Nunca' },
          ],
          scoring: { scale: 'SF2', transform: 'FIVEPT' },
        },
      ],
    },
  ],
};

// Escala de Dolor MEFIR (0-10)
export const DOLOR_MEFIR_SCHEMA = {
  id: 'dolor_mefir',
  name: 'Escala de Dolor MEFIR',
  fullName: 'Escala Visual Analógica de Dolor MEFIR',
  description: 'Evaluación del nivel de dolor en diferentes zonas del cuerpo usando una escala del 0 al 10.',
  estimatedTime: '2-3 minutos',
  category: 'dolor',
  version: '1.0',
  scoring: {
    type: 'average',
    method: 'mean',
  },
  sections: [
    {
      id: 'dolor_actual',
      title: 'Nivel de Dolor Actual',
      description: 'Indique el nivel de dolor que siente EN ESTE MOMENTO en cada zona. 0 = Sin dolor, 10 = Dolor máximo imaginable.',
      questions: [
        {
          id: 'dolor_cervical',
          text: 'Zona Cervical (cuello)',
          type: QUESTION_TYPES.FACE_SCALE,
          required: true,
          min: 0,
          max: 10,
          labels: {
            0: 'Sin dolor',
            5: 'Dolor moderado',
            10: 'Dolor máximo',
          },
          scoring: { zone: 'cervical', weight: 1 },
        },
        {
          id: 'dolor_dorsal',
          text: 'Zona Dorsal (espalda alta)',
          type: QUESTION_TYPES.FACE_SCALE,
          required: true,
          min: 0,
          max: 10,
          labels: {
            0: 'Sin dolor',
            5: 'Dolor moderado',
            10: 'Dolor máximo',
          },
          scoring: { zone: 'dorsal', weight: 1 },
        },
        {
          id: 'dolor_lumbar',
          text: 'Zona Lumbar (espalda baja)',
          type: QUESTION_TYPES.FACE_SCALE,
          required: true,
          min: 0,
          max: 10,
          labels: {
            0: 'Sin dolor',
            5: 'Dolor moderado',
            10: 'Dolor máximo',
          },
          scoring: { zone: 'lumbar', weight: 1 },
        },
        {
          id: 'dolor_hombros',
          text: 'Hombros',
          type: QUESTION_TYPES.FACE_SCALE,
          required: true,
          min: 0,
          max: 10,
          labels: {
            0: 'Sin dolor',
            5: 'Dolor moderado',
            10: 'Dolor máximo',
          },
          scoring: { zone: 'hombros', weight: 1 },
        },
        {
          id: 'dolor_rodillas',
          text: 'Rodillas',
          type: QUESTION_TYPES.FACE_SCALE,
          required: true,
          min: 0,
          max: 10,
          labels: {
            0: 'Sin dolor',
            5: 'Dolor moderado',
            10: 'Dolor máximo',
          },
          scoring: { zone: 'rodillas', weight: 1 },
        },
        {
          id: 'dolor_general',
          text: 'Dolor General / Global',
          type: QUESTION_TYPES.FACE_SCALE,
          required: true,
          min: 0,
          max: 10,
          labels: {
            0: 'Sin dolor',
            5: 'Dolor moderado',
            10: 'Dolor máximo',
          },
          scoring: { zone: 'general', weight: 2 }, // Mayor peso para dolor general
        },
      ],
    },
    {
      id: 'observaciones',
      title: 'Observaciones Adicionales',
      questions: [
        {
          id: 'dolor_notas',
          text: '¿Hay alguna otra zona donde sienta dolor? Descríbala brevemente:',
          type: QUESTION_TYPES.TEXTAREA,
          required: false,
          placeholder: 'Ej: Dolor en tobillo derecho desde hace 2 semanas...',
          maxLength: 500,
        },
      ],
    },
  ],
};

// Documento legal de consentimiento
export const CONSENT_DOCUMENT = {
  id: 'consent_evaluacion',
  title: 'Consentimiento Informado para Evaluación Clínica',
  version: '1.0',
  content: `
CONSENTIMIENTO INFORMADO PARA EVALUACIÓN CLÍNICA

Yo, el/la paciente abajo firmante, declaro que:

1. INFORMACIÓN RECIBIDA
He sido informado/a de forma clara y comprensible sobre la naturaleza y el propósito de esta evaluación clínica, que tiene como objetivo recopilar información sobre mi estado de salud física y mental para mejorar mi tratamiento.

2. VOLUNTARIEDAD
Mi participación es completamente voluntaria. Puedo retirarme en cualquier momento sin que esto afecte la calidad de mi atención médica.

3. CONFIDENCIALIDAD
Entiendo que toda la información proporcionada será tratada de forma confidencial y solo será utilizada con fines clínicos, cumpliendo con la normativa vigente de protección de datos (RGPD).

4. DERECHOS
He sido informado/a de mis derechos de acceso, rectificación, cancelación y oposición respecto a mis datos personales.

5. PREGUNTAS
He tenido la oportunidad de hacer preguntas y todas ellas han sido respondidas satisfactoriamente.

Al firmar este documento, confirmo que he leído y comprendido toda la información anterior y doy mi consentimiento para participar en esta evaluación clínica.
  `.trim(),
};

// Lista de todos los cuestionarios disponibles
export const AVAILABLE_QUESTIONNAIRES = [
  {
    id: 'sf12',
    schema: SF12_SCHEMA,
    icon: 'heart',
    color: 'blue',
  },
  {
    id: 'dolor_mefir',
    schema: DOLOR_MEFIR_SCHEMA,
    icon: 'activity',
    color: 'orange',
  },
];

// Función para obtener todas las preguntas de un schema
export const getAllQuestions = (schema) => {
  const questions = [];
  schema.sections.forEach(section => {
    section.questions.forEach(question => {
      questions.push({
        ...question,
        sectionId: section.id,
        sectionTitle: section.title,
      });
    });
  });
  return questions;
};

// Nombres completos de las 8 escalas SF-12
const SF12_SCALE_NAMES = {
  PF: 'Función Física',
  RP: 'Rol Físico',
  BP: 'Dolor Corporal',
  GH: 'Salud General',
  VT: 'Vitalidad',
  SF: 'Función Social',
  RE: 'Rol Emocional',
  MH: 'Salud Mental',
};

// ============================================================
// Algoritmo oficial NEMC/UCLA para SF-12v1
// Fuente: Ware, Kosinski & Keller (1996)
// https://labs.dgsom.ucla.edu/hays/files/view/docs/programs-utilities/sf12v1_short.sas.txt
// ============================================================

// PASO 1: Transformaciones de escala raw → 0-100
const SF12_TRANSFORMS = {
  RFIVEPT: { 1: 100, 2: 75, 3: 50, 4: 25, 5: 0 },           // Reversed 5-point
  THREEPT: { 1: 0, 2: 50, 3: 100 },                           // 3-point direct
  TWOPT:   { 1: 0, 2: 100 },                                  // 2-point direct
  RSIXPT:  { 1: 100, 2: 80, 3: 60, 4: 40, 5: 20, 6: 0 },    // Reversed 6-point
  SIXPT:   { 1: 0, 2: 20, 3: 40, 4: 60, 5: 80, 6: 100 },    // 6-point direct
  FIVEPT:  { 1: 0, 2: 25, 3: 50, 4: 75, 5: 100 },            // 5-point direct
};

// PASO 2: Pesos de regresión oficiales para variables dummy
// Cada escala genera N-1 dummies (todos los niveles excepto el máximo)
const SF12_PCS_WEIGHTS = {
  PF02: { 0: -7.23216, 50: -3.45555 },
  PF04: { 0: -6.24397, 50: -2.73557 },
  RP2:  { 0: -4.61617 },
  RP3:  { 0: -5.51747 },
  BP2:  { 0: -11.25544, 25: -8.38063, 50: -6.50522, 75: -3.80130 },
  GH1:  { 0: -8.37399, 25: -5.56461, 50: -3.02396, 75: -1.31872 },
  EN2:  { 0: -2.44706, 20: -2.02168, 40: -1.61850, 60: -1.14387, 80: -0.42251 },
  SF2:  { 0: -0.33682, 25: -0.94342, 50: -0.18043, 75: 0.11038 },
  RE2:  { 0: 3.04365 },
  RE3:  { 0: 2.32091 },
  EM3:  { 0: 3.46638, 20: 2.90426, 40: 2.37241, 60: 1.36689, 80: 0.66514 },
  EM4:  { 0: 4.61446, 20: 3.41593, 40: 2.34247, 60: 1.28044, 80: 0.41188 },
};

const SF12_MCS_WEIGHTS = {
  PF02: { 0: 3.93115, 50: 1.86840 },
  PF04: { 0: 2.68282, 50: 1.43103 },
  RP2:  { 0: 1.44060 },
  RP3:  { 0: 1.66968 },
  BP2:  { 0: 1.48619, 25: 1.76691, 50: 1.49384, 75: 0.90384 },
  GH1:  { 0: -1.71175, 25: -0.16891, 50: 0.03482, 75: -0.06064 },
  EN2:  { 0: -6.02409, 20: -4.88962, 40: -3.29805, 60: -1.65178, 80: -0.92057 },
  SF2:  { 0: -6.29724, 25: -8.26066, 50: -5.63286, 75: -3.13896 },
  RE2:  { 0: -6.82672 },
  RE3:  { 0: -5.69921 },
  EM3:  { 0: -10.19085, 20: -7.92717, 40: -6.31121, 60: -4.09842, 80: -1.94949 },
  EM4:  { 0: -16.15395, 20: -10.77911, 40: -8.09914, 60: -4.59055, 80: -1.95934 },
};

// Constantes de intercept para T-scores
const PCS12_INTERCEPT = 56.57706;
const MCS12_INTERCEPT = 60.75781;

// Mapeo de escala interna → escala SF-36 para escalas 0-100
const SF12_SCALE_MAP = {
  GH1:  'GH',
  PF02: 'PF',
  PF04: 'PF',
  RP2:  'RP',
  RP3:  'RP',
  RE2:  'RE',
  RE3:  'RE',
  BP2:  'BP',
  EM3:  'MH',
  EN2:  'VT',
  EM4:  'MH',
  SF2:  'SF',
};

// Función para calcular puntuación SF-12 — Algoritmo oficial NEMC/UCLA
export const calculateSF12Score = (answers) => {
  const allQuestions = getAllQuestions(SF12_SCHEMA);

  // PASO 1: Transformar respuestas raw → escala 0-100
  const transformed = {};
  allQuestions.forEach(q => {
    const raw = answers[q.id];
    if (raw === undefined || !q.scoring) return;
    const map = SF12_TRANSFORMS[q.scoring.transform];
    if (!map) return;
    transformed[q.scoring.scale] = map[raw] ?? null;
  });

  // Verificar que tenemos las 12 respuestas
  const requiredScales = ['GH1','PF02','PF04','RP2','RP3','RE2','RE3','BP2','EM3','EN2','EM4','SF2'];
  const missing = requiredScales.filter(s => transformed[s] === undefined || transformed[s] === null);
  // 🔴 SIN LAS 12, NO HAY PUNTUACIÓN. Ann, 28-ago-2026: «si vamos a hacer
  // cuestionarios para tener resultados científicos, no podemos avanzar de
  // manera incorrecta; no nos servirá de nada».
  //
  // Aquí se toleraban hasta 3 preguntas en blanco y se calculaba igual. Y lo
  // peor no era calcular: era CÓMO. Una pregunta sin contestar aporta peso 0,
  // que en esta regresión es la categoría de referencia — o sea, cuenta como
  // si el paciente hubiera respondido LO MEJOR posible. El resultado salía
  // mejor de lo real, con toda la pinta de ser válido y sin avisar a nadie.
  //
  // El algoritmo oficial no lo permite: RAWPCS12 es una SUMA de los doce
  // términos y, en SAS, si un solo dato falta la suma entera queda vacía
  // (sf12v1_short.sas, líneas 300-314). Incompleto = sin puntuación. No se
  // imputa, no se estima, no se aproxima.
  //
  // Un dato a medias no solo no sirve: contamina los que sí valen, porque
  // luego nadie sabe cuál era cuál.
  if (missing.length > 0) {
    return {
      pcs: null, mcs: null, total: null, scales: {}, scaleNames: SF12_SCALE_NAMES,
      incompleto: true, faltan: missing,
      interpretation: {
        pcs: `Sin puntuación · faltan ${missing.length} de 12 respuestas`,
        mcs: `Sin puntuación · faltan ${missing.length} de 12 respuestas`,
      },
    };
  }

  // PASO 2: Crear variables dummy y calcular RAWPCS12 / RAWMCS12
  let rawPCS = 0;
  let rawMCS = 0;

  requiredScales.forEach(scale => {
    const val = transformed[scale];
    if (val === undefined || val === null) return;

    // PCS dummies
    const pcsWeights = SF12_PCS_WEIGHTS[scale] || {};
    Object.keys(pcsWeights).forEach(level => {
      const numLevel = parseFloat(level);
      if (val === numLevel) {
        rawPCS += pcsWeights[level];
      }
    });

    // MCS dummies
    const mcsWeights = SF12_MCS_WEIGHTS[scale] || {};
    Object.keys(mcsWeights).forEach(level => {
      const numLevel = parseFloat(level);
      if (val === numLevel) {
        rawMCS += mcsWeights[level];
      }
    });
  });

  // PASO 3: T-scores finales
  const pcs = Math.round((rawPCS + PCS12_INTERCEPT) * 100) / 100;
  const mcs = Math.round((rawMCS + MCS12_INTERCEPT) * 100) / 100;

  // Escalas individuales 0-100 (promedio si hay 2 ítems por escala)
  const scaleAccum = {};
  const scaleCountMap = {};
  requiredScales.forEach(s => {
    const sf36Scale = SF12_SCALE_MAP[s];
    if (!sf36Scale || transformed[s] === undefined || transformed[s] === null) return;
    scaleAccum[sf36Scale] = (scaleAccum[sf36Scale] || 0) + transformed[s];
    scaleCountMap[sf36Scale] = (scaleCountMap[sf36Scale] || 0) + 1;
  });
  const scales = {};
  Object.keys(SF12_SCALE_NAMES).forEach(k => {
    scales[k] = scaleCountMap[k] ? Math.round(scaleAccum[k] / scaleCountMap[k]) : null;
  });

  return {
    pcs: Math.round(pcs),   // Physical Component Summary (T-score)
    mcs: Math.round(mcs),   // Mental Component Summary (T-score)
    total: Math.round((pcs + mcs) / 2),
    pcs_exact: pcs,          // Valor exacto para investigación
    mcs_exact: mcs,
    scales,                  // 8 escalas individuales (0-100)
    scaleNames: SF12_SCALE_NAMES,
    interpretation: {
      pcs: pcs >= 50 ? 'Buena salud física' : pcs >= 40 ? 'Salud física moderada' : 'Salud física comprometida',
      mcs: mcs >= 50 ? 'Buena salud mental' : mcs >= 40 ? 'Salud mental moderada' : 'Salud mental comprometida',
    },
  };
};

// Función para calcular puntuación de dolor
export const calculateDolorScore = (answers) => {
  const questions = getAllQuestions(DOLOR_MEFIR_SCHEMA).filter(q => q.type === QUESTION_TYPES.FACE_SCALE);
  let totalWeighted = 0;
  let totalWeight = 0;
  const zones = {};

  questions.forEach(question => {
    const answer = answers[question.id];
    /* Antes bastaba con `!== undefined`. Un valor `null` pasaba el filtro y
       `null * peso` da 0 — o sea, esa zona entraba en la media como «no me
       duele nada». Un hueco se contaba como ausencia de dolor. */
    if (answer !== undefined && answer !== null && !Number.isNaN(Number(answer))) {
      const weight = question.scoring?.weight || 1;
      totalWeighted += Number(answer) * weight;
      totalWeight += weight;
      
      if (question.scoring?.zone) {
        zones[question.scoring.zone] = answer;
      }
    }
  });

  /* 🔴 SIN RESPUESTAS NO HAY MEDIA (Ann, 28-ago-2026).
     Antes devolvía 0, y en una escala de dolor el 0 significa «no me duele
     nada»: el mejor resultado posible. Un cuestionario de dolor sin contestar
     salía como una paciente sin dolor. Es el tercero con el mismo fallo
     —SF-12, ODI y este—, cada uno con su forma. */
  if (totalWeight <= 0) {
    return { average: null, zones: {}, maxZone: {}, incompleto: true };
  }
  const averageScore = Math.round((totalWeighted / totalWeight) * 10) / 10;

  return {
    average: averageScore,
    zones,
    maxZone: Object.entries(zones).reduce((max, [zone, value]) => 
      value > (max.value || 0) ? { zone, value } : max, {}),
    interpretation: averageScore <= 2 ? 'Dolor leve o ausente' : 
                   averageScore <= 5 ? 'Dolor moderado' : 
                   averageScore <= 7 ? 'Dolor significativo' : 'Dolor severo',
  };
};


// Cuestionario EVA (Escala Visual Analógica) - Para pre-sesión
export const EVA_SCHEMA = {
  id: 'eva',
  name: 'EVA',
  fullName: 'Escala Visual Analógica del Dolor',
  description: 'Escala simple de 0-10 para medir la intensidad del dolor actual.',
  estimatedTime: '1 minuto',
  category: 'dolor',
  version: '1.0',
  scoring: {
    type: 'simple',
    method: 'direct',
  },
  sections: [
    {
      id: 'eva_dolor',
      title: 'Intensidad del Dolor',
      questions: [
        {
          id: 'eva_actual',
          text: '¿Cuál es su nivel de dolor en este momento?',
          type: QUESTION_TYPES.SCALE,
          required: true,
          min: 0,
          max: 10,
          labels: {
            0: 'Sin dolor',
            2: 'Leve',
            4: 'Moderado',
            6: 'Intenso',
            8: 'Muy intenso',
            10: 'Máximo dolor imaginable',
          },
          scoring: { weight: 1 },
        },
      ],
    },
  ],
};

// Función para calcular el score EVA
export const calculateEVAScore = (answers) => {
  const score = answers['eva_actual'] || 0;
  return {
    score,
    interpretation: score === 0 ? 'Sin dolor' :
                   score <= 2 ? 'Dolor leve' :
                   score <= 4 ? 'Dolor moderado' :
                   score <= 6 ? 'Dolor intenso' :
                   score <= 8 ? 'Dolor muy intenso' : 'Dolor máximo',
  };
};

// Cuestionario VAS Oficial (Visual Analogue Scale) - 0-100mm
export const VAS_OFICIAL_SCHEMA = {
  id: 'vas_oficial',
  name: 'EVA / VAS dolor (0-100 mm)',
  fullName: 'Escala Visual Analógica del Dolor - VAS Oficial',
  description: 'Escala visual analógica oficial de 0-100mm para medición precisa del dolor. El paciente marca un punto en una línea continua.',
  estimatedTime: '30 segundos',
  category: 'dolor',
  version: '1.0',
  isVAS: true,  // Flag para identificar que usa componente VAS especial
  scoring: {
    type: 'direct',
    method: 'position',
    range: [0, 100],
    unit: 'mm'
  },
  interpretation: {
    ranges: [
      { min: 0, max: 20, label: 'Dolor leve', color: 'green' },
      { min: 21, max: 40, label: 'Dolor leve-moderado', color: 'lime' },
      { min: 41, max: 60, label: 'Dolor moderado', color: 'yellow' },
      { min: 61, max: 80, label: 'Dolor moderado-severo', color: 'orange' },
      { min: 81, max: 100, label: 'Dolor severo', color: 'red' },
    ]
  },
  sections: [
    {
      id: 'vas_section',
      title: 'Intensidad del Dolor',
      description: 'Marque en la línea el punto que mejor representa su nivel de dolor actual',
      questions: [
        {
          id: 'vas_pain_mm',
          text: 'Indique su nivel de dolor actual marcando un punto en la línea',
          type: 'vas_scale',  // Tipo especial para VAS
          required: true,
          min: 0,
          max: 100,
          leftLabel: 'Sin dolor',
          rightLabel: 'Peor dolor imaginable',
          unit: 'mm',
          scoring: { weight: 1 },
        },
      ],
    },
  ],
};

// Función para calcular el score VAS
export const calculateVASScore = (answers) => {
  const score = answers['vas_pain_mm'];
  if (score === null || score === undefined) {
    return { score: null, interpretation: 'Sin respuesta' };
  }
  
  let interpretation;
  let severity;
  
  if (score <= 20) {
    interpretation = 'Dolor leve';
    severity = 'mild';
  } else if (score <= 40) {
    interpretation = 'Dolor leve-moderado';
    severity = 'mild-moderate';
  } else if (score <= 60) {
    interpretation = 'Dolor moderado';
    severity = 'moderate';
  } else if (score <= 80) {
    interpretation = 'Dolor moderado-severo';
    severity = 'moderate-severe';
  } else {
    interpretation = 'Dolor severo';
    severity = 'severe';
  }
  
  return {
    score,
    interpretation,
    severity,
    unit: 'mm',
  };
};
